import React from "react";
import { FiCheck, FiX, FiRefreshCw } from "react-icons/fi";
import { supabase } from "../../supabaseClient";

const FileList = ({ files, loading, onFileClick, onBack, sessionName, searchQuery = "", filterType = "all" }) => {

  // Filter files based on search and filter
  const getFilteredFiles = () => {
    let filtered = [...files];

    // Apply filter type
    if (filterType !== "all") {
      filtered = filtered.filter(f => f.type === filterType);
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(f => f.name.toLowerCase().includes(query));
    }

    return filtered;
  };

  const filteredFiles = getFilteredFiles();

  const isImageFile = (fileName, type) => {
    const ext = fileName.split('.').pop().toLowerCase();
    return type === "images" || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
  };

  const getImageUrl = (file) => {
    const { data } = supabase.storage.from("Storage").getPublicUrl(file.fullPath);
    return data.publicUrl;
  };

  const getFileIcon = (fileName, type) => {
    const ext = fileName.split('.').pop().toLowerCase();

    // Images
    if (type === "images" || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) {
      return { icon: "bi-file-image", color: "#4CAF50" };
    }
    // PDF
    if (ext === 'pdf') {
      return { icon: "bi-file-pdf", color: "#F44336" };
    }
    // Audio
    if (type === "audios" || ['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(ext)) {
      return { icon: "bi-file-music", color: "#9C27B0" };
    }
    // Video
    if (['mp4', 'webm', 'mov', 'avi'].includes(ext)) {
      return { icon: "bi-file-play", color: "#FF5722" };
    }
    // Word
    if (['doc', 'docx'].includes(ext)) {
      return { icon: "bi-file-word", color: "#2196F3" };
    }
    // Excel
    if (['xls', 'xlsx'].includes(ext)) {
      return { icon: "bi-file-excel", color: "#4CAF50" };
    }
    // Text/CSV
    if (['txt', 'csv'].includes(ext)) {
      return { icon: "bi-file-text", color: "#607D8B" };
    }
    // Default
    return { icon: "bi-file-earmark", color: "#9E9E9E" };
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDisplayName = (fileName) => {
    // Remove timestamp suffix if present (e.g., filename_1234567890.ext -> filename.ext)
    const ext = fileName.split('.').pop();
    const nameWithoutExt = fileName.replace(`.${ext}`, '');
    const cleanName = nameWithoutExt.replace(/_\d{13}$/, '');
    return cleanName.length > 30 ? `${cleanName.substring(0, 30)}...${ext}` : `${cleanName}.${ext}`;
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "60px 0",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <FiRefreshCw
            style={{
              fontSize: "32px",
              color: "var(--teal-bg)",
              animation: "spin 1s linear infinite",
            }}
          />
          <span style={{ opacity: 0.7 ,color:"var(--text)" }}>Loading sessions...</span>
        </div>
      </div>
    );
  }

  // Group filtered files by type
  const groupedFiles = {
    images: filteredFiles.filter(f => f.type === "images"),
    documents: filteredFiles.filter(f => f.type === "documents"),
    audios: filteredFiles.filter(f => f.type === "audios")
  };

  const renderFileGroup = (groupName, groupFiles, icon, title) => {
    if (groupFiles.length === 0) return null;

    return (
      <div className="fm-file-group" key={groupName}>
        <h6 className="fm-group-title">
          <i className={`bi ${icon} me-2`}></i>
          {title} ({groupFiles.length})
        </h6>
        <div className="fm-files-grid">
          {groupFiles.map((file, index) => {
            const { icon: fileIcon, color } = getFileIcon(file.name, file.type);
            const isImage = isImageFile(file.name, file.type);
            return (
              <div
                key={`${file.name}-${index}`}
                className="fm-file-card"
                onClick={() => onFileClick(file)}
              >
                {isImage ? (
                  <div className="fm-file-thumbnail">
                    <img
                      src={getImageUrl(file)}
                      alt={file.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<i class="bi bi-file-image" style="color: #4CAF50; font-size: 20px;"></i>';
                      }}
                    />
                  </div>
                ) : (
                  <div className="fm-file-icon" style={{ backgroundColor: `${color}20` }}>
                    <i className={`bi ${fileIcon}`} style={{ color: color }}></i>
                  </div>
                )}
                <div className="fm-file-info">
                  <span className="fm-file-name" title={file.name}>
                    {getDisplayName(file.name)}
                  </span>
                  <div className="fm-file-meta">
                    <span>{formatFileSize(file.metadata?.size)}</span>
                    <span className="fm-dot">•</span>
                    <span>{formatDate(file.created_at)}</span>
                  </div>
                </div>
                <div className="fm-file-actions">
                  <i className="bi bi-eye"></i>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="fm-file-list">
      {/* Header */}
      <div className="fm-file-list-header">
        <button className="fm-back-btn" onClick={onBack}>
          <i className="bi bi-arrow-left me-2"></i>
          Back
        </button>
        <div className="fm-file-count">
          <i className="bi bi-files me-1"></i>
          {filteredFiles.length} of {files.length} file{files.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Files Content */}
      {filteredFiles.length === 0 ? (
        <div className="fm-empty-state">
          <i className="bi bi-file-earmark-x"></i>
          <h5>{files.length === 0 ? "No Files" : "No Results"}</h5>
          <p>{files.length === 0 ? "This session doesn't have any uploaded files." : "No files match your search or filter."}</p>
        </div>
      ) : (
        <div className="fm-file-groups">
          {renderFileGroup("images", groupedFiles.images, "bi-images", "Images")}
          {renderFileGroup("documents", groupedFiles.documents, "bi-file-earmark-text", "Documents")}
          {renderFileGroup("audios", groupedFiles.audios, "bi-music-note-beamed", "Audio Files")}
        </div>
      )}
    </div>
  );
};

export default FileList;
